import React from 'react';
import { Alert, Box, Grid, Stack, Typography } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';

import BudgetAllocationActivityGrid from './BudgetAllocationActivityGrid';
import BudgetAllocationBulkDialog from './BudgetAllocationBulkDialog';
import BudgetAllocationFilterBar from './BudgetAllocationFilterBar';
import BudgetAllocationLineItemGrid from './BudgetAllocationLineItemGrid';
import BudgetAllocationLinksGrid from './BudgetAllocationLinksGrid';
import BudgetAllocationSummaryPanel from './BudgetAllocationSummaryPanel';
import {
  fetchActivities,
  fetchActivityBudgetControl,
  fetchCostCenters,
  fetchLineItems,
  fetchLinks,
  fetchSummary,
  processAllocation,
  unmapLinks,
} from './budgetallocation.apis';
import {
  AllocationMode,
  IAllocationActivity,
  IAllocationBudgetControl,
  IAllocationCostCenter,
  IAllocationLineItem,
  IAllocationLink,
  IAllocationSummary,
} from './budgetallocation.models';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';
import { PlanningWorkbenchShell } from '../planningcore';
import FPlanningJourney from '../core/FPlanningJourney';

const emptySelection = (): GridRowSelectionModel =>
  ({ type: 'include', ids: new Set() }) as GridRowSelectionModel;
const selectionCount = (s: GridRowSelectionModel) => s?.ids?.size ?? 0;
const selectionIds = (s: GridRowSelectionModel) =>
  Array.from(s?.ids ?? []).map(String);

type State = {
  loading: boolean;
  error: string;
  success: string;
  warning: string;
  phases: any[];
  costcenters: IAllocationCostCenter[];
  selectedCostCenter: number | null;
  activitySearch: string;
  lineItemSearch: string;
  activities: IAllocationActivity[];
  lineitems: IAllocationLineItem[];
  links: IAllocationLink[];
  selectedActivities: GridRowSelectionModel;
  selectedLineItems: GridRowSelectionModel;
  selectedLinks: GridRowSelectionModel;
  dialogOpen: boolean;
  summary: IAllocationSummary;
  control: IAllocationBudgetControl;
};

class BudgetAllocationWorkbench extends React.Component<any, State> {
  state: State = {
    loading: false,
    error: '',
    success: '',
    warning: '',
    phases: [],
    costcenters: [],
    selectedCostCenter: null,
    activitySearch: '',
    lineItemSearch: '',
    activities: [],
    lineitems: [],
    links: [],
    selectedActivities: emptySelection(),
    selectedLineItems: emptySelection(),
    selectedLinks: emptySelection(),
    dialogOpen: false,
    summary: {},
    control: {
      controlareacode: 'ACTIVITY',
      ceilingamount: 0,
      allocatedamount: 0,
      availableamount: 0,
      utilisationpercent: 0,
      warningpercent: 90,
      controlstatus: 'UNAVAILABLE',
    },
  };

  get budgetplanid() {
    return this.props?.params?.budgetplanid ?? this.props?.params?.bpi ?? null;
  }

  componentDidMount() {
    this.initialize();
  }

  private getPageParam = (key: string): unknown => {
    const params: any = this.props.params ?? {};
    return params[key] ?? params?.get?.(key) ?? undefined;
  };

  private getPlanningCycleId = (): number => {
    //const p = (this.props.params || {}) as any;
    //return Number(p.planningcycleid ?? p.pci ?? p.planningCycleId ?? 0);
    const value = this.props.plancontext?.pci;
    return Number(value || 0);
  };

  private initialize = async () => {
    const planningcycleid = this.getPlanningCycleId();

    if (!planningcycleid) {
      this.setState({
        error: 'Planning Cycle context is missing. Please set your preferred context in the Planning Setup .',
      });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      const [phases] = await Promise.all([fetchPlanningJourneyPhases()]);
      this.setState({ phases });
      await this.loadInitial();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };
  
  loadInitial = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const pci = this.getPlanningCycleId();
      const [costcenters, control] = await Promise.all([
        fetchCostCenters(pci, this.budgetplanid),
        fetchActivityBudgetControl(pci),
      ]);
      this.setState({
        costcenters: costcenters || [],
        control,
        selectedCostCenter: null,
        activities: [],
        lineitems: [],
        links: [],
        summary: {},
      });
    } catch (e: any) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  clearContext = () =>
    this.setState({
      activities: [],
      lineitems: [],
      links: [],
      summary: {},
      selectedActivities: emptySelection(),
      selectedLineItems: emptySelection(),
      selectedLinks: emptySelection(),
    });

  refreshSelectedCostCenter = async () => {
    const { selectedCostCenter, activitySearch, lineItemSearch } = this.state;
    console.log('selectedcc is ', selectedCostCenter);
    if (!selectedCostCenter) {
      this.clearContext();
      return;
    }
    this.setState({ loading: true, error: '', success: '' });

    try {
      const [activities, lineitems, links, summary] = await Promise.all([
        fetchActivities(this.getPlanningCycleId(), selectedCostCenter, activitySearch, this.budgetplanid),
        fetchLineItems(this.getPlanningCycleId(), selectedCostCenter, lineItemSearch, this.budgetplanid),
        fetchLinks(this.getPlanningCycleId(), selectedCostCenter, this.budgetplanid),
        fetchSummary(this.getPlanningCycleId(), selectedCostCenter, this.budgetplanid),
      ]);
      this.setState({
        activities,
        lineitems,
        links,
        summary,
        selectedActivities: emptySelection(),
        selectedLineItems: emptySelection(),
        selectedLinks: emptySelection(),
      });
    } catch (e: any) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  onCostCenterChange = (selectedCostCenter: number | null) =>
    this.setState({ selectedCostCenter }, this.refreshSelectedCostCenter);

  onActivitySearchChange = (activitySearch: string) =>
    this.setState({ activitySearch }, this.refreshSelectedCostCenter);

  onLineItemSearchChange = (lineItemSearch: string) =>
    this.setState({ lineItemSearch }, this.refreshSelectedCostCenter);
  
  submitAllocation = async (mode: AllocationMode, totalamount: number) => {
    const activityplanids = selectionIds(this.state.selectedActivities);
    const lineitemids = selectionIds(this.state.selectedLineItems);
    if (
      !this.state.selectedCostCenter ||
      !activityplanids.length ||
      !lineitemids.length
    ) {
      this.setState({
        error:
          'Select a Cost Centre, at least one Activity Plan and at least one account.',
      });
      return;
    }
    if (activityplanids.length > 1 && lineitemids.length > 1) {
      this.setState({
        error:
          'Many-to-many allocation requires the allocation matrix and is not available in the quick dialog.',
      });
      return;
    }
    this.setState({ loading: true, error: '', success: '' });
    try {
      await processAllocation({
        planningcycleid: this.getPlanningCycleId(),
        costcenterid: this.state.selectedCostCenter,
        budgetplanid: this.budgetplanid,
        activityplanids,
        lineitemids,
        mode,
        totalamount,
      });
      this.setState({
        success: `Allocation processed for ${activityplanids.length} Activity Plan(s) and ${lineitemids.length} account(s).`,
        dialogOpen: false,
      });
      await this.refreshSelectedCostCenter();
    } catch (e: any) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };
  unmapSelected = async () => {
    const ids = selectionIds(this.state.selectedLinks);
    if (!ids.length) {
      this.setState({ error: 'Select one or more allocation links to unmap.' });
      return;
    }
    this.setState({ loading: true, error: '', success: '' });
    try {
      await unmapLinks(this.getPlanningCycleId(), ids);
      this.setState({ success: `${ids.length} allocation link(s) unmapped.` });
      await this.refreshSelectedCostCenter();
    } catch (e: any) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };
  render() {
    const s = this.state;
    const active = !!s.selectedCostCenter;

    const filters = (
      <BudgetAllocationFilterBar
        costcenters={s.costcenters}
        selectedCostCenter={s.selectedCostCenter}
        activitySearch={s.activitySearch}
        lineItemSearch={s.lineItemSearch}
        selectedActivityCount={selectionCount(s.selectedActivities)}
        selectedLineItemCount={selectionCount(s.selectedLineItems)}
        selectedLinkCount={selectionCount(s.selectedLinks)}
        loading={s.loading}
        onCostCenterChange={this.onCostCenterChange}
        onActivitySearchChange={this.onActivitySearchChange}
        onLineItemSearchChange={this.onLineItemSearchChange}
        onRefresh={this.refreshSelectedCostCenter}
        onOpenAllocate={() => {
          if (!s.control.budgetcontrolid) {
            this.setState({
              error: 'An active Activity Budget Control ceiling is required before allocation.',
            });
            return;
          }
          if (s.control.controlstatus === 'EXCEEDED') {
            this.setState({
              error: 'The Activity Budget Control ceiling is already exceeded. Revise Budget Control before allocating.',
            });
            return;
          }
          this.setState({ dialogOpen: true });
        }}
        onUnmapSelected={this.unmapSelected}
      />
    );

    return (
      <PlanningWorkbenchShell
        {...this.props}
        onBack={this.props.onBack}
        headerExtension={
          <FPlanningJourney
            phases={this.state.phases}
            //currentPhaseCode={wb?.context.phasecode || 'FORECAST_BASELINE'}
            currentPhaseCode={'BUDGET ALLOCATION'}
          />
        }

        title={this.props.title || 'Budget Allocation'}
        subtitle="Map certified Activity Plans to Budget Accounts within the active Activity Budget Control ceiling."
        icon={<AccountBalanceOutlinedIcon />}
        loading={s.loading}
        error={s.error}
        onRefresh={this.loadInitial}
        filters={filters}
      >
      <Box sx={{ p: 3 }}>
        <Stack sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Activity Budget Allocation Workbench
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Allocate certified annual Activity Plans to six-digit Budget Plan
            accounts.
          </Typography>
        </Stack>
        {/* <BudgetAllocationFilterBar
          costcenters={s.costcenters}
          selectedCostCenter={s.selectedCostCenter}
          activitySearch={s.activitySearch}
          lineItemSearch={s.lineItemSearch}
          selectedActivityCount={selectionCount(s.selectedActivities)}
          selectedLineItemCount={selectionCount(s.selectedLineItems)}
          selectedLinkCount={selectionCount(s.selectedLinks)}
          loading={s.loading}
          onCostCenterChange={this.onCostCenterChange}
          onActivitySearchChange={this.onActivitySearchChange}
          onLineItemSearchChange={this.onLineItemSearchChange}
          onRefresh={this.refreshSelectedCostCenter}
          onOpenAllocate={() => {
          if (!s.control.budgetcontrolid) {
            this.setState({
              error: 'An active Activity Budget Control ceiling is required before allocation.',
            });
            return;
          }
          if (s.control.controlstatus === 'EXCEEDED') {
            this.setState({
              error: 'The Activity Budget Control ceiling is already exceeded. Revise Budget Control before allocating.',
            });
            return;
          }
          this.setState({ dialogOpen: true });
        }}
          onUnmapSelected={this.unmapSelected}
        /> */}
        {s.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {s.error}
          </Alert>
        )}
        {s.success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {s.success}
          </Alert>
        )}
        {!active ? (
          <Alert severity="info">
            Select an active Cost Centre to load eligible Activity Plans,
            accounts and allocation links.
          </Alert>
        ) : (
          <Stack spacing={3}>
            <BudgetAllocationSummaryPanel summary={s.summary} control={s.control} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <BudgetAllocationActivityGrid
                  rows={s.activities}
                  selection={s.selectedActivities}
                  loading={s.loading}
                  onSelectionChange={(selectedActivities) =>
                    this.setState({ selectedActivities })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <BudgetAllocationLineItemGrid
                  rows={s.lineitems}
                  selection={s.selectedLineItems}
                  loading={s.loading}
                  onSelectionChange={(selectedLineItems) =>
                    this.setState({ selectedLineItems })
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <BudgetAllocationLinksGrid
                  rows={s.links}
                  selection={s.selectedLinks}
                  loading={s.loading}
                  onSelectionChange={(selectedLinks) =>
                    this.setState({ selectedLinks })
                  }
                />
              </Grid>
            </Grid>
          </Stack>
        )}
        <BudgetAllocationBulkDialog
          open={s.dialogOpen}
          activityCount={selectionCount(s.selectedActivities)}
          lineItemCount={selectionCount(s.selectedLineItems)}
          controlAvailable={s.control.availableamount}
          onClose={() => this.setState({ dialogOpen: false })}
          onSubmit={this.submitAllocation}
        />
      </Box>
      </PlanningWorkbenchShell>
    );
  }
}
export default BudgetAllocationWorkbench;
